import React, { useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { SplitText } from 'gsap/SplitText';
import { useGSAP } from '@gsap/react';
import { getLenis } from '../utils/lenis';

gsap.registerPlugin(ScrollTrigger, SplitText);

const PARALLAX_LAYERS = [
  { layer: '1', yPercent: 70 },
  { layer: '2', yPercent: 55 },
  { layer: '3', yPercent: 40 },
  { layer: '4', yPercent: 22 },
  { layer: '5', yPercent: 12 },
];

function TYSHero() {
  const heroRef = useRef(null);

  useGSAP(
    () => {
      const mm = gsap.matchMedia();

      mm.add(
        '(min-width: 992px) and (pointer: fine) and (prefers-reduced-motion: no-preference)',
        () => {
        gsap.utils
          .toArray('[data-parallax-layers]', heroRef.current)
          .forEach((triggerElement) => {
            const tl = gsap.timeline({
              scrollTrigger: {
                trigger: triggerElement,
                start: 'top top',
                end: 'bottom top',
                scrub: true,
                invalidateOnRefresh: true,
              },
            });

            PARALLAX_LAYERS.forEach((layerObj, idx) => {
              const targets = triggerElement.querySelectorAll(
                `[data-parallax-layer="${layerObj.layer}"]`
              );

              if (!targets.length) {
                return;
              }

              tl.to(
                targets,
                {
                  yPercent: layerObj.yPercent,
                  ease: 'none',
                },
                idx === 0 ? 0 : '<'
              );
            });
          });
        }
      );

      return () => mm.revert();
    },
    { scope: heroRef }
  );

  useGSAP(
    () => {
      const heading = heroRef.current?.querySelector('[data-split="heading"]');

      if (!heading) {
        return undefined;
      }

      let splitInstance;
      let cancelled = false;
      const mm = gsap.matchMedia();

      const initReveal = () => {
        if (cancelled) {
          return;
        }

        gsap.set(heading, { autoAlpha: 1 });

        splitInstance = SplitText.create(heading, {
          type: 'lines, words, chars',
          autoSplit: true,
          mask: 'lines',
          linesClass: 'line',
          wordsClass: 'word',
          charsClass: 'letter',
          onSplit(instance) {
            return gsap.from(instance.chars, {
              yPercent: 110,
              autoAlpha: 0,
              duration: 0.78,
              stagger: 0.012,
              ease: 'expo.out',
              delay: 0.12,
            });
          },
        });
      };

      mm.add('(prefers-reduced-motion: no-preference)', () => {
        if (document.fonts?.ready) {
          document.fonts.ready.then(initReveal);
          return;
        }

        initReveal();
      });

      mm.add('(prefers-reduced-motion: reduce)', () => {
        gsap.set(heading, { autoAlpha: 1 });
      });

      return () => {
        cancelled = true;
        splitInstance?.revert();
        mm.revert();
      };
    },
    { scope: heroRef }
  );

  const scrollToSection = () => {
    const lenis = getLenis();

    if (lenis) {
      lenis.scrollTo('#scroll', { offset: -50, duration: 1.75 });
      return;
    }

    const target = document.querySelector('#scroll');

    if (!target) {
      return;
    }

    const targetTop = target.getBoundingClientRect().top + window.scrollY - 50;
    window.scrollTo({ top: targetTop, behavior: 'smooth' });
  };

  return (
    <section className='tys-hero' ref={heroRef}>
      <div className='parallax'>
        <div className='parallax__header'>
          <div className='parallax__visuals'>
            <div className='parallax__black-line-overflow'></div>
            <div data-parallax-layers className='parallax__layers'>
              <img
                className='parallax__layer-img parallax__layer-img--sky'
                data-parallax-layer='1'
                src={process.env.PUBLIC_URL + '/images/tys-sky.png'}
                alt=''
              />
              <img
                className='parallax__layer-img parallax__layer-img--mountains'
                data-parallax-layer='2'
                src={process.env.PUBLIC_URL + '/images/tys-mountains.png'}
                alt=''
              />
              <div
                className='parallax__layer-title'
                data-parallax-layer='3'
              >
                <h1
                  className='parallax__title'
                  data-split='heading'
                  data-split-reveal='chars'
                >
                  T<span>HE</span> Y<span>OUNG</span> S
                  <span>AMARITAN</span>
                </h1>
              </div>
              <img
                className='parallax__layer-img parallax__layer-img--boy'
                data-parallax-layer='4'
                src={process.env.PUBLIC_URL + '/images/tys-boy.png'}
                alt=''
              />
              <img
                className='parallax__layer-img parallax__layer-img--rock'
                data-parallax-layer='5'
                src={process.env.PUBLIC_URL + '/images/tys-rock.png'}
                alt=''
              />
            </div>
            <div className='parallax__fade'></div>
          </div>
          <div className='parallax__cover-meta'>
            <span className='parallax__cover-rule' aria-hidden='true'></span>
            <p className='parallax__author'>J. Schuyler Sprowles</p>
            <span className='parallax__cover-rule' aria-hidden='true'></span>
          </div>
        </div>
        <div className='scroll-container'>
          <button
            type='button'
            className='scroll'
            onClick={scrollToSection}
            aria-label='Scroll to book details'
          >
            <p>Scroll</p>
            <img src={process.env.PUBLIC_URL + '/images/chevron-down.svg'} alt='' />
          </button>
        </div>
      </div>
    </section>
  );
}

export default TYSHero;
